// Right-side slide-out drawer listing Vela's current suggestions.
// Each suggestion has a primary action and Skip. Clicking a primary action calls back to the parent.

import React from "react";
import { X } from "lucide-react";
import type { VelaSuggestion } from "../types";

interface VelaSuggestionsDrawerProps {
  open: boolean;
  onClose: () => void;
  suggestions: VelaSuggestion[];
  onPrimary: (s: VelaSuggestion) => void;
  onSkip: (s: VelaSuggestion) => void;
}

export default function VelaSuggestionsDrawer({ open, onClose, suggestions, onPrimary, onSkip }: VelaSuggestionsDrawerProps) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/40 z-[999] animate-[vsdFade_0.25s_ease-out]" />
      <aside className="fixed top-0 right-0 bottom-0 w-full md:w-[480px] max-w-full bg-white z-[1000] shadow-[-8px_0_32px_rgba(14,26,26,0.15)] flex flex-col animate-[vsdSlide_0.3s_ease-out]">
        <div className="px-5 md:px-6 py-4 border-b border-lineSoft flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#6B46C1] text-white text-[14px] font-extrabold inline-flex items-center justify-center shadow-[0_2px_8px_rgba(124,58,237,0.25)]">V</span>
            <div>
              <h2 className="text-base font-extrabold text-ink m-0">Vela's suggestions</h2>
              <div className="text-xs text-ink3 mt-0.5 font-medium">
                {suggestions.length} {suggestions.length === 1 ? "thing" : "things"} worth looking at
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-lg bg-transparent border-none cursor-pointer text-ink3 flex items-center justify-center hover:bg-lineSoft">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 md:px-6 py-2 pb-8">
          {suggestions.length === 0 ? (
            <div className="py-14 px-5 text-center text-ink4 text-[13px] font-medium">
              Nothing to review right now. Vela will flag items as your chart grows.
            </div>
          ) : (
            suggestions.map((s) => {
              const typeColor = s.type === "archive" ? "text-amber-700" : s.type === "merge" ? "text-sky-800" : "text-vela-deep";
              return (
                <div key={s.id} className="py-4 border-b border-lineSoft last:border-b-0 first:pt-2">
                  <div className={`text-[10.5px] font-extrabold uppercase tracking-wider mb-1.5 ${typeColor}`}>{s.typeLabel}</div>
                  <h4 className="text-[13.5px] font-bold text-ink m-0 mb-1 leading-tight">{s.title}</h4>
                  <p className="text-[12px] text-ink3 m-0 mb-3 leading-relaxed">{s.description}</p>
                  <div className="flex justify-end items-center gap-3">
                    <button type="button" onClick={() => onSkip(s)} className="text-[12.5px] text-ink3 underline bg-transparent border-none cursor-pointer font-medium">Skip</button>
                    <button type="button" onClick={() => onPrimary(s)} className="h-9 px-4 rounded-lg text-[12.5px] font-semibold border-none bg-ink text-white cursor-pointer hover:bg-ink2">{s.primaryAction}</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <style>{`
          @keyframes vsdFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes vsdSlide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        `}</style>
      </aside>
    </>
  );
}